import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, FileText, Phone, CheckCircle, XCircle, Trash2, Settings as SettingsIcon, Building2, CreditCard, TrendingUp, Clock, AlertTriangle, ScrollText, Plus, UserCog, Coins, LogIn, FolderOpen, Euro, Mail } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Double confirmation helper for destructive actions.
 * Shows two sequential window.confirm() prompts. Returns true only when both are confirmed.
 * Use for ELIMINA / VERIFICA / RESET / similar irreversible actions.
 */
function confirmTwice(firstMessage, secondMessage) {
  if (!window.confirm(firstMessage)) return false;
  return window.confirm(secondMessage || '⚠️ ULTIMA CONFERMA — Sei davvero sicuro? L\'operazione non potrà essere annullata.');
}

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [imprese, setImprese] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [bankTransfers, setBankTransfers] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderRequests, setOrderRequests] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [footer, setFooter] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('requests');
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [s, pr, opr, i, c, bt, st, rf, po, rv, oreq, supp, ft, sc, pdocs, nav] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/requests/pending`, { withCredentials: true }),
        axios.get(`${API}/admin/requests/open`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/imprese`, { withCredentials: true }),
        axios.get(`${API}/admin/consultations`, { withCredentials: true }),
        axios.get(`${API}/admin/bank-transfers`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/refund-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/purchase-orders`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/revenue`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/order-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/support`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/footer`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/site-content`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/documents/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/nav-config`, { withCredentials: true }).catch(() => ({ data: { tree: null } })),
      ]);
      setStats(s.data);
      setPendingRequests(pr.data);
      setOpenRequests(opr.data);
      setImprese(i.data);
      setConsultations(c.data);
      setBankTransfers(bt.data);
      setSettings(st.data);
      setRefunds(rf.data);
      setOrders(po.data);
      setRevenue(rv.data);
      setOrderRequests(oreq.data);
      setSupportTickets(supp.data);
      setFooter(ft.data);
      setSiteContent(sc.data);
      setPendingDocs(pdocs.data);
      setNavConfig(nav.data?.tree || null);
    } catch (e) {
      toast.error('Errore caricamento dati admin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const tabCounts = useMemo(() => ({
    'requests': pendingRequests.length,
    'open-requests': openRequests.filter(r => r.stato === 'open').length,
    'imprese': imprese.length,
    'bank-transfers': bankTransfers.filter(b => b.payment_status === 'pending_verification').length,
    'order-requests': orderRequests.filter(o => o.stato === 'richiesto').length,
    'documents': pendingDocs.length,
    'refunds': refunds.filter(r => r.stato === 'pending').length,
    'orders': orders.length,
    'support': supportTickets.filter(t => t.stato === 'aperto').length,
  }), [pendingRequests, openRequests, imprese, bankTransfers, orderRequests, pendingDocs, refunds, orders, supportTickets]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EA580C]" /></div>;
  }



  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <DashboardHeader />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2">Dashboard Admin</h1>
          <p className="text-slate-600">Gestisci richieste, imprese, consulenze e impostazioni</p>
        </div>

        <StatsGrid stats={stats} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8 space-y-6">
          <DynamicTabsList
            navConfig={navConfig}
            counts={tabCounts}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />


          <TabsContent value="requests" className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste da Approvare</h2>
            {pendingRequests.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessuna richiesta in attesa</p>
                </CardContent>
              </Card>
            ) : (
              pendingRequests.map((req) => <AdminRequestCard key={req.id} req={req} onUpdate={loadAll} />)
            )}
          </TabsContent>

          <TabsContent value="open-requests" className="space-y-4">
            <OpenRequestsSection requests={openRequests} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="revenue" className="space-y-4">
            <RevenueSection revenue={revenue} />
          </TabsContent>

          <TabsContent value="imprese" className="space-y-4">
            <Tabs defaultValue="all" className="space-y-4">
              <TabsList className="bg-slate-100 border border-slate-200" data-testid="imprese-subtabs">
                <TabsTrigger value="all" data-testid="impr-sub-all">Tutte le imprese</TabsTrigger>
                <TabsTrigger value="inactive-12" data-testid="impr-sub-inactive-12">Inattive 12+ mesi</TabsTrigger>
                <TabsTrigger value="inactive-24" data-testid="impr-sub-inactive-24">Inattive 24+ mesi</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="space-y-4">
                <h2 className="text-2xl font-semibold text-[#0F172A]">Gestione Imprese</h2>
                {imprese.map((imp) => <AdminImpresaCard key={imp.id} impresa={imp} onUpdate={loadAll} requiredDocs={settings?.required_documents || []} />)}
              </TabsContent>
              <TabsContent value="inactive-12" className="space-y-4">
                <InactiveUsersSection months={12} role="impresa" />
              </TabsContent>
              <TabsContent value="inactive-24" className="space-y-4">
                <InactiveUsersSection months={24} role="impresa" />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="consultations" className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#0F172A]">Consulenze Telefoniche</h2>
            {consultations.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessuna consulenza</p>
                </CardContent>
              </Card>
            ) : (
              consultations.map((c) => <AdminConsultationCard key={c.id} cons={c} onUpdate={loadAll} />)
            )}
          </TabsContent>

          <TabsContent value="bank-transfers" className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#0F172A]">Bonifici Bancari</h2>
            {bankTransfers.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessun bonifico</p>
                </CardContent>
              </Card>
            ) : (
              bankTransfers.map((bt) => <AdminBankTransferCard key={bt.id} transfer={bt} onUpdate={loadAll} />)
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <SettingsPanel settings={settings} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="refunds" className="space-y-4">
            <RefundsAdminSection refunds={refunds} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <OrdersAdminSection orders={orders} imprese={imprese} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="paid-consultations" className="space-y-4">
            <PaidConsultationsSection />
          </TabsContent>

          <TabsContent value="inactive-12" className="space-y-4">
            <InactiveUsersSection months={12} />
          </TabsContent>

          <TabsContent value="inactive-24" className="space-y-4">
            <InactiveUsersSection months={24} />
          </TabsContent>

          <TabsContent value="order-requests" className="space-y-4">
            <OrderRequestsAdminSection orderRequests={orderRequests} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <DocumentsAdminSection docs={pendingDocs} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="support" className="space-y-4">
            <SupportAdminSection tickets={supportTickets} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="footer-settings" className="space-y-4">
            <FooterSettingsPanel footer={footer} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="site-content" className="space-y-4">
            <SiteContentPanel site={siteContent} onUpdate={loadAll} />
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <Tabs defaultValue="all" className="space-y-4">
              <TabsList className="bg-slate-100 border border-slate-200" data-testid="users-subtabs">
                <TabsTrigger value="all" data-testid="users-sub-all">Tutti gli utenti</TabsTrigger>
                <TabsTrigger value="inactive-12" data-testid="users-sub-inactive-12">Inattivi 12+ mesi</TabsTrigger>
                <TabsTrigger value="inactive-24" data-testid="users-sub-inactive-24">Inattivi 24+ mesi</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="space-y-4">
                <UsersSection />
              </TabsContent>
              <TabsContent value="inactive-12" className="space-y-4">
                <InactiveUsersSection months={12} />
              </TabsContent>
              <TabsContent value="inactive-24" className="space-y-4">
                <InactiveUsersSection months={24} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="profile" className="space-y-4">
            <AdminProfileSection />
          </TabsContent>

          <TabsContent value="trash" className="space-y-4">
            <TrashSection />
          </TabsContent>

          <TabsContent value="layout" className="space-y-4">
            <NavConfigEditor initialTree={navConfig} onSave={(t) => setNavConfig(t)} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const StatsGrid = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Utenti Totali', value: stats.total_users, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: 'Imprese Verificate', value: `${stats.verified_imprese}/${stats.total_imprese}`, icon: Building2, color: 'text-purple-600 bg-purple-50' },
    { label: 'Richieste Pending', value: stats.pending_requests, icon: Clock, color: 'text-yellow-600 bg-yellow-50' },
    { label: 'Richieste Aperte', value: stats.open_requests, icon: FileText, color: 'text-green-600 bg-green-50' },
    { label: 'Consulenze Pagate', value: stats.total_consultations, icon: Phone, color: 'text-pink-600 bg-pink-50' },
    { label: 'Bonifici Pending', value: stats.pending_bank_transfers, icon: CreditCard, color: 'text-orange-600 bg-orange-50' },
    { label: 'Revenue Totale', value: `€${stats.revenue.toFixed(2)}`, icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="stats-grid">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="bg-white border border-slate-200 rounded-xl">
            <CardContent className="p-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${c.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-xs text-slate-500 mb-1">{c.label}</p>
              <p className="text-xl font-bold text-[#0F172A]">{c.value}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const AdminRequestCard = ({ req, onUpdate }) => {
  const [priceEuros, setPriceEuros] = useState('');
  const handleApprove = async () => {
    const v = parseFloat((priceEuros || '').replace(',', '.'));
    if (isNaN(v) || v < 0) {
      toast.error('Inserisci un prezzo valido in € (es. 35.50)');
      return;
    }
    const cents = Math.round(v * 100);
    try {
      await axios.post(`${API}/admin/requests/${req.id}/approve`, { purchase_price_cents: cents }, { withCredentials: true });
      toast.success(`Richiesta approvata — prezzo €${(cents/100).toFixed(2)}`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleReject = async () => {
    try {
      await axios.post(`${API}/admin/requests/${req.id}/reject`, {}, { withCredentials: true });
      toast.success('Richiesta rifiutata');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };
  const handleDelete = async () => {
    if (!confirmTwice('RIMOZIONE TOTALE: la richiesta sarà eliminata anche dalle imprese che l\'hanno comprata. Confermi?')) return;
    try {
      await axios.delete(`${API}/admin/requests/${req.id}`, { withCredentials: true });
      toast.success('Richiesta eliminata totalmente');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  const tipologiaLabels = { esterni: 'Esterni', appartamento: 'Appartamento', bagno: 'Bagno', cucina: 'Cucina', altro: 'Altro' };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-request-${req.id}`}>
      <CardHeader>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {req.tipologia_lavoro && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{tipologiaLabels[req.tipologia_lavoro] || req.tipologia_lavoro}</span>
          )}
        </div>
        <CardTitle className="text-xl text-[#0F172A]">{req.titolo}</CardTitle>
        <CardDescription>{req.location_roma} {req.budget && `• Budget: €${req.budget}`}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-slate-700 mb-3">{req.descrizione}</p>
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-slate-50 rounded-md px-3 py-2">
            <span className="text-slate-500">Immobile in possesso?</span>
            <p className="font-medium text-[#0F172A]">{req.immobile_posseduto === true ? 'Sì' : req.immobile_posseduto === false ? 'No' : '—'}</p>
          </div>
          <div className="bg-slate-50 rounded-md px-3 py-2">
            <span className="text-slate-500">Progetto pronto?</span>
            <p className="font-medium text-[#0F172A]">{req.progetto_pronto === true ? 'Sì' : req.progetto_pronto === false ? 'No' : '—'}</p>
          </div>
        </div>
        {req.proprietario && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
            <p><strong>Proprietario:</strong> {req.proprietario.name}</p>
            <p><strong>Email:</strong> {req.proprietario.email}</p>
            <p><strong>Tel:</strong> {req.proprietario.phone}</p>
          </div>
        )}
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex flex-col">
            <Label className="text-xs text-slate-600 mb-1">Prezzo vendita contatto (€)</Label>
            <Input
              data-testid={`price-input-${req.id}`}
              type="number"
              step="0.01"
              min="0"
              value={priceEuros}
              onChange={(e) => setPriceEuros(e.target.value)}
              placeholder="es. 35.50"
              className="rounded-lg w-36"
            />
          </div>
          <Button data-testid={`approve-btn-${req.id}`} onClick={handleApprove} className="bg-green-600 hover:bg-green-700 text-white">
            <CheckCircle className="w-4 h-4 mr-2" /> Approva
          </Button>
          <Button data-testid={`reject-btn-${req.id}`} onClick={handleReject} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-2" /> Rifiuta
          </Button>
          <Button data-testid={`delete-btn-${req.id}`} onClick={handleDelete} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" /> Elimina totalmente
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminImpresaCard = ({ impresa, onUpdate, requiredDocs }) => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  
  const handleVerify = async () => {
    if (!confirmTwice(
      `Verificare l'impresa "${impresa.ragione_sociale || impresa.name}"? L'impresa potrà operare anche se non ha tutti i documenti approvati.`,
      '⚠️ Ultima conferma: vuoi davvero verificare questa impresa?'
    )) return;
    try {
      const { data } = await axios.post(`${API}/admin/imprese/${impresa.id}/verify`, {}, { withCredentials: true });
      const warnings = [];
      if (data.missing?.length) warnings.push(`Mancanti: ${data.missing.join(', ')}`);
      if (data.expired?.length) warnings.push(`Scaduti: ${data.expired.join(', ')}`);
      if (data.rejected?.length) warnings.push(`Rifiutati: ${data.rejected.join(', ')}`);
      if (warnings.length) {
        toast.success(`Impresa verificata. Nota documenti: ${warnings.join(' • ')}`);
      } else {
        toast.success('Impresa verificata');
      }
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleUnverify = async () => {
    if (!confirmTwice(
      `Rimuovere la verifica dall'impresa "${impresa.ragione_sociale || impresa.name}"? Non potrà più acquistare richieste.`,
      '⚠️ Ultima conferma: vuoi davvero rimuovere la verifica?'
    )) return;
    try {
      await axios.post(`${API}/admin/imprese/${impresa.id}/unverify`, {}, { withCredentials: true });
      toast.success('Verifica rimossa');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };
  
  const handleImpersonate = async () => {
    try {
      await axios.post(`${API}/admin/impersonate/${impresa.id}`, {}, { withCredentials: true });
      await checkAuth();
      toast.success(`Stai navigando come ${impresa.ragione_sociale || impresa.name}`);
      navigate('/dashboard');
    } catch (e) { toast.error('Errore impersonation'); }
  };

  const handleDeleteImpresa = async () => {
    const label = impresa.ragione_sociale || impresa.name || impresa.email;
    if (!confirmTwice(
      `Eliminare definitivamente l'azienda "${label}"?\n\nVerranno cancellati: documenti, acquisti, richieste ordini, anagrafica. Lo storico transazioni di pagamento sarà conservato per audit.`,
      `⚠️ ULTIMA CONFERMA: stai per eliminare "${label}". L'operazione non può essere annullata.`
    )) return;
    try {
      await axios.delete(`${API}/admin/users/${impresa.id}`, { withCredentials: true });
      toast.success(`Azienda "${label}" spostata nel cestino. Puoi ripristinarla o eliminarla definitivamente dalla tab "Cestino".`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  
  const docTypes = impresa.documents?.map(d => d.tipo) || [];

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-impresa-${impresa.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl text-[#0F172A]">
              {impresa.ragione_sociale || impresa.name}
            </CardTitle>
            <CardDescription className="text-xs space-y-0.5 mt-1">
              <div>{impresa.email} • {impresa.phone}</div>
              {impresa.referente && <div>Referente: {impresa.referente}</div>}
              {impresa.partita_iva && <div>P.IVA: {impresa.partita_iva} • CF: {impresa.codice_fiscale}</div>}
              {impresa.sede_legale && <div>Sede: {impresa.sede_legale}</div>}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-slate-500" data-testid={`impresa-balance-${impresa.id}`}>€{((impresa.credits_cents ?? (impresa.credits || 0) * 100) / 100).toFixed(2)} saldo</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              impresa.email_verified ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
            }`}>{impresa.email_verified ? 'Email ✓' : 'Email ✗'}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              impresa.verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>{impresa.verified ? 'Verificata' : 'Non verificata'}</span>
          </div>
        </div>
        {impresa.last_login_at && (
          <p className="text-xs text-slate-500 mt-2" data-testid={`impresa-last-login-${impresa.id}`}>
            Ultimo accesso: {new Date(impresa.last_login_at).toLocaleString('it-IT')}
          </p>
        )}
        {!impresa.last_login_at && (
          <p className="text-xs text-slate-400 italic mt-2" data-testid={`impresa-last-login-${impresa.id}`}>Mai effettuato l'accesso</p>
        )}
      </CardHeader>
      <CardContent>
        {impresa.company && (
          <p className="text-slate-700 text-sm mb-3">{impresa.company.descrizione}</p>
        )}
        <div className="mb-4">
          <p className="text-sm font-medium text-[#0F172A] mb-2">Documenti ({(impresa.documents || []).filter(d => d.approval_status === 'approved').length}/{requiredDocs.length} approvati)</p>
          <div className="flex flex-wrap gap-2">
            {requiredDocs.map((dt) => {
              const doc = impresa.documents?.find(d => d.tipo === dt);
              // Compute visual state: missing, rejected, expired, pending, approved
              let state = 'missing';
              if (doc) {
                const st = doc.approval_status || 'pending';
                if (st === 'rejected') state = 'rejected';
                else if (st === 'approved') state = 'approved';
                else state = 'pending';
              }
              const styles = {
                approved: 'bg-green-100 text-green-700 hover:bg-green-200',
                pending: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
                rejected: 'bg-red-100 text-red-700 hover:bg-red-200',
                missing: 'bg-red-50 text-red-600 border border-red-200',
              };
              const Icon = state === 'approved' ? CheckCircle : (state === 'pending' ? Clock : XCircle);
              const label = state === 'missing' ? `${dt} (mancante)` : state === 'rejected' ? `${dt} (rifiutato)` : state === 'pending' ? `${dt} (in attesa)` : dt;
              return (
                <a
                  key={dt}
                  href={doc ? `${API}/documents/${doc.id}/download` : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!doc) e.preventDefault(); }}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs ${styles[state]} ${doc ? 'cursor-pointer' : 'cursor-default'}`}
                  data-testid={`doc-link-${impresa.id}-${dt}`}
                  title={label}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </a>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!impresa.verified ? (
            <Button data-testid={`verify-impresa-${impresa.id}`} onClick={() => handleVerify()} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle className="w-4 h-4 mr-2" /> Verifica
            </Button>
          ) : (
            <Button data-testid={`unverify-impresa-${impresa.id}`} onClick={() => handleUnverify()} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-2" /> Rimuovi verifica
            </Button>
          )}
          <Button data-testid={`assign-credits-${impresa.id}`} onClick={() => setShowCreditsModal(true)} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <Coins className="w-4 h-4 mr-2" /> Assegna crediti
          </Button>
          <Button data-testid={`impersonate-${impresa.id}`} onClick={handleImpersonate} size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
            <LogIn className="w-4 h-4 mr-2" /> Naviga come impresa
          </Button>
          <Button data-testid={`delete-impresa-${impresa.id}`} onClick={handleDeleteImpresa} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" /> Elimina azienda
          </Button>
        </div>
      </CardContent>

      <AssignCreditsModal impresa={impresa} open={showCreditsModal} onClose={() => setShowCreditsModal(false)} onSuccess={() => { setShowCreditsModal(false); onUpdate(); }} />
    </Card>
  );
};

const AssignCreditsModal = ({ impresa, open, onClose, onSuccess }) => {
  const [credits, setCredits] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/imprese/assign-credits`, {
        impresa_user_id: impresa.id, credits: parseInt(credits), motivo
      }, { withCredentials: true });
      toast.success('Crediti assegnati');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="assign-credits-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Assegna crediti</DialogTitle>
          <CardDescription>{impresa.ragione_sociale || impresa.name} ({impresa.credits || 0} crediti attuali)</CardDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Crediti da aggiungere (negativo per rimuovere)</Label>
            <Input data-testid="assign-credits-input" type="number" value={credits} onChange={(e) => setCredits(e.target.value)} required className="rounded-lg" />
          </div>
          <div>
            <Label>Motivo / note</Label>
            <Textarea data-testid="assign-credits-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} className="rounded-lg" />
          </div>
          <Button type="submit" data-testid="assign-credits-submit" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Salvataggio...' : 'Conferma'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminConsultationCard = ({ cons, onUpdate }) => {
  const handleComplete = async () => {
    try {
      await axios.post(`${API}/admin/consultations/${cons.id}/complete`, {}, { withCredentials: true });
      toast.success('Consulenza completata');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-cons-${cons.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-[#0F172A]">€{cons.prezzo}</p>
            <p className="text-sm text-slate-600 mt-1">{cons.descrizione}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            cons.stato === 'paid' ? 'bg-green-100 text-green-700' :
            cons.stato === 'completed' ? 'bg-blue-100 text-blue-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {cons.stato}
          </span>
        </div>
        {cons.proprietario && cons.stato === 'paid' && (
          <div className="bg-slate-50 rounded-lg p-3 mb-3 text-sm">
            <p><strong>Cliente:</strong> {cons.proprietario.name}</p>
            <p><strong>Email:</strong> {cons.proprietario.email}</p>
            <p><strong>Tel:</strong> {cons.proprietario.phone}</p>
          </div>
        )}
        {cons.stato === 'paid' && (
          <Button data-testid={`complete-cons-${cons.id}`} onClick={handleComplete} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
            <CheckCircle className="w-4 h-4 mr-2" /> Segna come Completata
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const AdminBankTransferCard = ({ transfer, onUpdate }) => {
  const handleConfirm = async () => {
    try {
      await axios.post(`${API}/admin/bank-transfers/${transfer.id}/confirm`, {}, { withCredentials: true });
      toast.success('Bonifico confermato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };
  const handleReject = async () => {
    try {
      await axios.post(`${API}/admin/bank-transfers/${transfer.id}/reject`, {}, { withCredentials: true });
      toast.success('Bonifico rifiutato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-bt-${transfer.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-[#0F172A]">€{transfer.amount} - {transfer.type}</p>
            <p className="text-xs text-slate-500">Causale: {transfer.id}</p>
            {transfer.user && (
              <p className="text-sm text-slate-600 mt-1">
                {transfer.user.name} ({transfer.user.email}) - {transfer.user.role}
              </p>
            )}
            {transfer.metadata?.credits && (
              <p className="text-sm text-slate-600 mt-1">Crediti da accreditare: {transfer.metadata.credits}</p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            transfer.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
            transfer.payment_status === 'rejected' ? 'bg-red-100 text-red-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {transfer.payment_status}
          </span>
        </div>
        {transfer.payment_status === 'pending_verification' && (
          <div className="flex gap-2">
            <Button data-testid={`confirm-bt-${transfer.id}`} onClick={handleConfirm} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle className="w-4 h-4 mr-2" /> Conferma Ricezione
            </Button>
            <Button data-testid={`reject-bt-${transfer.id}`} onClick={handleReject} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-2" /> Rifiuta
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SettingsPanel = ({ settings, onUpdate }) => {
  const [purchasePrice, setPurchasePrice] = useState(settings?.request_purchase_price || 35);
  const [consultationPriceEur, setConsultationPriceEur] = useState(((settings?.consultation_price_cents ?? 5000) / 100).toFixed(2));
  const [maxPurchases, setMaxPurchases] = useState(settings?.max_purchases_per_request || 4);
  const [bankInfo, setBankInfo] = useState(settings?.bank_account_info || '');
  const [docs, setDocs] = useState((settings?.required_documents || []).join('\n'));
  const [packages, setPackages] = useState(JSON.stringify(settings?.credit_packages || [], null, 2));
  const [stripeKey, setStripeKey] = useState(settings?.stripe_api_key || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      let parsedPackages;
      try {
        parsedPackages = JSON.parse(packages);
      } catch {
        toast.error('JSON pacchetti non valido');
        setSaving(false);
        return;
      }
      await axios.put(`${API}/admin/settings`, {
        request_purchase_price: parseInt(purchasePrice),
        consultation_price_cents: Math.round(parseFloat(consultationPriceEur) * 100),
        max_purchases_per_request: parseInt(maxPurchases),
        bank_account_info: bankInfo,
        required_documents: docs.split('\n').map(s => s.trim()).filter(Boolean),
        credit_packages: parsedPackages,
        stripe_api_key: stripeKey,
      }, { withCredentials: true });
      toast.success('Impostazioni salvate');
      onUpdate();
    } catch (e) {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid="settings-panel">
      <CardHeader>
        <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
          <SettingsIcon className="w-5 h-5" /> Impostazioni Piattaforma
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Prezzo Acquisto Richiesta (crediti)</Label>
            <Input data-testid="setting-purchase-price" type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} className="rounded-lg" />
          </div>
          <div>
            <Label>Prezzo Consulenza (€)</Label>
            <Input data-testid="setting-consultation-price" type="number" step="0.01" value={consultationPriceEur} onChange={(e) => setConsultationPriceEur(e.target.value)} className="rounded-lg" />
          </div>
          <div>
            <Label>Max Imprese per Richiesta</Label>
            <Input data-testid="setting-max-purchases" type="number" value={maxPurchases} onChange={(e) => setMaxPurchases(e.target.value)} className="rounded-lg" />
          </div>
        </div>
        <div>
          <Label>Documenti Richiesti (uno per riga)</Label>
          <Textarea data-testid="setting-required-docs" value={docs} onChange={(e) => setDocs(e.target.value)} rows={4} className="rounded-lg font-mono text-sm" />
        </div>
        <div>
          <Label>Pacchetti Crediti (JSON)</Label>
          <Textarea data-testid="setting-packages" value={packages} onChange={(e) => setPackages(e.target.value)} rows={6} className="rounded-lg font-mono text-sm" />
        </div>
        <div>
          <Label>Informazioni Bonifico Bancario</Label>
          <Textarea data-testid="setting-bank-info" value={bankInfo} onChange={(e) => setBankInfo(e.target.value)} rows={4} className="rounded-lg" />
        </div>
        <div>
          <Label>Stripe Secret Key (es. sk_test_... o sk_live_...)</Label>
          <Input
            data-testid="setting-stripe-key"
            type="password"
            value={stripeKey}
            onChange={(e) => setStripeKey(e.target.value)}
            placeholder="Lascia vuoto per usare la chiave di default del sistema"
            className="rounded-lg font-mono"
          />
          <p className="text-xs text-slate-500 mt-1">Imposta qui la chiave Stripe del tuo wallet. Se vuota, viene usata la chiave di default.</p>
        </div>
        <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
          {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
        </Button>
      </CardContent>
    </Card>
  );
};

const RefundsAdminSection = ({ refunds, onUpdate }) => {
  const handleApprove = async (id) => {
    if (!window.confirm('Approvare il rimborso? I crediti torneranno all\'impresa.')) return;
    try {
      await axios.post(`${API}/admin/refund-requests/${id}/approve`, {}, { withCredentials: true });
      toast.success('Rimborso approvato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };
  const handleReject = async (id) => {
    try {
      await axios.post(`${API}/admin/refund-requests/${id}/reject`, {}, { withCredentials: true });
      toast.success('Rimborso rifiutato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste di Rimborso</h2>
      {refunds.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna richiesta di rimborso</p>
          </CardContent>
        </Card>
      ) : (
        refunds.map((r) => (
          <Card key={r.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-refund-${r.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-[#0F172A]">{r.impresa?.name || 'Impresa'} ({r.impresa?.email})</p>
                  <p className="text-xs text-slate-500">Richiesta: {r.request_titolo || r.request_id}</p>
                  <p className="text-sm text-slate-700 mt-2">{r.motivo}</p>
                  <p className="text-xs text-slate-500 mt-1">Crediti da rimborsare: {r.credits_to_refund}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  r.stato === 'approved' ? 'bg-green-100 text-green-700' :
                  r.stato === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>{r.stato}</span>
              </div>
              {r.stato === 'pending' && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-refund-${r.id}`} onClick={() => handleApprove(r.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-2" /> Approva e rimborsa
                  </Button>
                  <Button data-testid={`reject-refund-${r.id}`} onClick={() => handleReject(r.id)} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                    <XCircle className="w-4 h-4 mr-2" /> Rifiuta
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

const OrdersAdminSection = ({ orders, imprese, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [selImpresa, setSelImpresa] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [importo, setImporto] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selImpresa) { toast.error('Seleziona impresa'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/purchase-orders`, {
        impresa_user_id: selImpresa,
        descrizione,
        importo_cents: Math.round(parseFloat(importo) * 100),
      }, { withCredentials: true });
      toast.success('Ordine emesso');
      setShowModal(false); setSelImpresa(''); setDescrizione(''); setImporto('');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-[#0F172A]">Ordini di Acquisto</h2>
        <Button data-testid="new-order-btn" onClick={() => setShowModal(true)} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Ordine
        </Button>
      </div>

      {orders.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <ScrollText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessun ordine emesso</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <Card key={o.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-order-${o.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-mono text-sm font-semibold text-[#EA580C]">{o.order_number}</p>
                  <p className="text-sm text-[#0F172A]">{o.descrizione}</p>
                  <p className="text-xs text-slate-500">Impresa: {o.impresa_name} ({o.impresa_email})</p>
                  <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString('it-IT')}</p>
                </div>
                <p className="text-lg font-bold text-[#0F172A]">€{o.importo}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="new-order-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Emetti Ordine di Acquisto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div>
              <Label>Impresa</Label>
              <select
                data-testid="order-impresa-select"
                value={selImpresa}
                onChange={(e) => setSelImpresa(e.target.value)}
                required
                className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Seleziona...</option>
                {imprese.map((i) => (
                  <option key={i.id} value={i.id}>{i.company?.nome || i.name} ({i.email})</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Descrizione ordine</Label>
              <Textarea data-testid="order-descrizione-input" value={descrizione} onChange={(e) => setDescrizione(e.target.value)} required rows={3} className="rounded-lg" />
            </div>
            <div>
              <Label>Importo (€)</Label>
              <Input data-testid="order-importo-input" type="number" step="0.01" value={importo} onChange={(e) => setImporto(e.target.value)} required className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="submit-order-btn" disabled={creating} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {creating ? 'Emissione...' : 'Emetti Ordine'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const UsersSection = () => {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('proprietario');
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const load = async (role) => {
    try {
      const { data } = await axios.get(`${API}/admin/users?role=${role}`, { withCredentials: true });
      setUsers(data);
    } catch (e) { toast.error('Errore caricamento utenti'); }
  };

  useEffect(() => { load(roleFilter); }, [roleFilter]);

  const handleImpersonate = async (u) => {
    try {
      await axios.post(`${API}/admin/impersonate/${u.id}`, {}, { withCredentials: true });
      await checkAuth();
      toast.success(`Navigando come ${u.name || u.email}`);
      navigate('/dashboard');
    } catch (e) { toast.error('Errore'); }
  };

  const handleDelete = async (u) => {
    const label = u.ragione_sociale || `${u.name || ''} ${u.cognome || ''}`.trim() || u.email;
    if (!confirmTwice(`Eliminare definitivamente l'utente "${label}"?\n\nVerranno cancellati anche i dati collegati (richieste, documenti, acquisti). Lo storico transazioni di pagamento sarà conservato.`)) return;
    const confirmText = window.prompt(`Per confermare digita "ELIMINA" (maiuscolo):`);
    if (confirmText !== 'ELIMINA') { toast.info('Eliminazione annullata'); return; }
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { withCredentials: true });
      toast.success(`Utente spostato nel cestino — ripristinabile dalla tab "Cestino"`);
      load(roleFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-[#0F172A]">Utenti</h2>
        <div className="flex gap-2">
          <Button data-testid="filter-proprietario" onClick={() => setRoleFilter('proprietario')} size="sm" variant={roleFilter === 'proprietario' ? 'default' : 'outline'} className={roleFilter === 'proprietario' ? 'bg-[#EA580C] text-white' : ''}>Proprietari</Button>
          <Button data-testid="filter-impresa" onClick={() => setRoleFilter('impresa')} size="sm" variant={roleFilter === 'impresa' ? 'default' : 'outline'} className={roleFilter === 'impresa' ? 'bg-[#EA580C] text-white' : ''}>Imprese</Button>
        </div>
      </div>

      {users.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessun utente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <Card key={u.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`user-row-${u.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-[#0F172A]">{u.ragione_sociale || `${u.name || ''} ${u.cognome || ''}`}</p>
                  <p className="text-xs text-slate-500">{u.email} • {u.phone}</p>
                  <p className="text-xs text-slate-400">Registrato: {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : ''}</p>
                  <p className="text-xs text-slate-500 mt-0.5" data-testid={`user-last-login-${u.id}`}>
                    {u.last_login_at
                      ? <>Ultimo accesso: <strong>{new Date(u.last_login_at).toLocaleString('it-IT')}</strong></>
                      : <span className="italic">Mai effettuato l'accesso</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${u.email_verified ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{u.email_verified ? 'Email ✓' : 'Non verif.'}</span>
                  {u.role === 'impresa' && (
                    <span className={`text-xs px-2 py-1 rounded ${u.verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{u.verified ? 'Verificata' : 'Pending'}</span>
                  )}
                  <Button data-testid={`impersonate-user-${u.id}`} onClick={() => handleImpersonate(u)} size="sm" variant="outline" className="border-blue-300 text-blue-700">
                    <LogIn className="w-3 h-3 mr-1" /> Naviga come
                  </Button>
                  <Button data-testid={`delete-user-${u.id}`} onClick={() => handleDelete(u)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3 h-3 mr-1" /> Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminProfileSection = () => {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [pwdEmail, setPwdEmail] = useState('');
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPwd, setLoadingPwd] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setLoadingEmail(true);
    try {
      await axios.post(`${API}/auth/change-email`, { new_email: newEmail, password: pwdEmail }, { withCredentials: true });
      toast.success('Email aggiornata');
      setNewEmail(''); setPwdEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setLoadingEmail(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoadingPwd(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: oldPwd, new_password: newPwd }, { withCredentials: true });
      toast.success('Password aggiornata');
      setOldPwd(''); setNewPwd('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setLoadingPwd(false); }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Profilo Admin</h2>
      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardContent className="p-6 space-y-1">
          <p className="text-sm text-slate-600">Email attuale: <strong>{user?.email}</strong></p>
          <p className="text-sm text-slate-600">Ruolo: <strong>admin</strong></p>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><UserCog className="w-5 h-5" /> Cambia Email</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangeEmail} className="space-y-3">
            <div>
              <Label>Nuova Email</Label>
              <Input data-testid="admin-new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Password attuale</Label>
              <Input data-testid="admin-email-confirm-pwd" type="password" value={pwdEmail} onChange={(e) => setPwdEmail(e.target.value)} required className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="admin-change-email-btn" disabled={loadingEmail} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {loadingEmail ? 'Salvataggio...' : 'Aggiorna email'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><UserCog className="w-5 h-5" /> Cambia Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div>
              <Label>Password attuale</Label>
              <Input data-testid="admin-old-pwd" type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Nuova Password (min 6 caratteri)</Label>
              <Input data-testid="admin-new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={6} className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="admin-change-pwd-btn" disabled={loadingPwd} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {loadingPwd ? 'Salvataggio...' : 'Aggiorna password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <FactoryResetCard />
      <ActiveOtpsCard />
    </div>
  );
};

const ActiveOtpsCard = () => {
  const [otps, setOtps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/active-otps`, { withCredentials: true });
      setOtps(data);
    } catch (e) { toast.error('Errore caricamento OTP'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const handleResend = async (email) => {
    setResending(email);
    try {
      await axios.post(`${API}/admin/active-otps/${encodeURIComponent(email)}/resend`, {}, { withCredentials: true });
      toast.success(`Nuovo OTP inviato a ${email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setResending(null); }
  };

  const handleCopy = (otp, email) => {
    try {
      navigator.clipboard.writeText(otp);
      toast.success(`OTP di ${email} copiato`);
    } catch { toast.info(`OTP: ${otp}`); }
  };

  return (
    <Card className="bg-white border-2 border-amber-300 rounded-xl" data-testid="active-otps-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
          <Mail className="w-5 h-5" /> Codici OTP attivi (failover SMTP)
        </CardTitle>
        <CardDescription className="text-amber-600">
          Se la consegna email non funziona (es. IP blacklist), puoi vedere qui i codici OTP attivi e comunicarli manualmente all'utente.
          Aggiornamento automatico ogni 15 secondi.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-slate-500 text-sm">Caricamento...</p> :
          otps.length === 0 ? (
            <p className="text-slate-500 text-sm italic">Nessun OTP attivo al momento.</p>
          ) : (
            <div className="space-y-2">
              {otps.map((o) => {
                const expiresAt = new Date(o.expires_at);
                const minutesLeft = Math.max(0, Math.round((expiresAt - new Date()) / 60000));
                return (
                  <div key={o.email} data-testid={`otp-row-${o.email}`} className="flex items-center justify-between gap-3 p-3 bg-amber-50/50 border border-amber-200 rounded-lg flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0F172A] text-sm">{o.name || '—'} {o.role && <span className="text-xs font-normal text-slate-500">({o.role})</span>}</p>
                      <p className="text-xs text-slate-500">{o.email}</p>
                      <p className="text-xs text-amber-700 mt-0.5">Scade tra ~{minutesLeft} min • Tentativi: {o.attempts}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <code className="text-2xl font-mono font-bold text-amber-700 bg-white px-3 py-1 rounded border border-amber-300 select-all" data-testid={`otp-code-${o.email}`}>
                        {o.otp}
                      </code>
                      <Button size="sm" variant="outline" onClick={() => handleCopy(o.otp, o.email)} className="border-amber-300">Copia</Button>
                      <Button size="sm" disabled={resending === o.email} onClick={() => handleResend(o.email)} className="bg-amber-600 hover:bg-amber-700 text-white">
                        {resending === o.email ? '...' : 'Rigenera'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </CardContent>
    </Card>
  );
};

const FactoryResetCard = () => {
  const [confirm, setConfirm] = useState('');
  const [keepAdmin, setKeepAdmin] = useState(true);
  const [keepSite, setKeepSite] = useState(true);
  const [keepFooter, setKeepFooter] = useState(true);
  const [keepSettings, setKeepSettings] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (confirm !== 'RESET') { toast.error('Digita "RESET" per confermare'); return; }
    if (!confirmTwice('⚠️ Questa operazione cancellerà TUTTI gli utenti (eccetto admin), richieste, ordini, documenti, recensioni e bonifici. Procedere?', '⚠️ ULTIMA CONFERMA: stai per AZZERARE tutti i dati operativi. Sei davvero sicuro?')) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/factory-reset`, {
        confirm: 'RESET',
        keep_admin: keepAdmin,
        keep_site_content: keepSite,
        keep_footer: keepFooter,
        keep_settings: keepSettings,
      }, { withCredentials: true });
      toast.success('Reset completato. Dati operativi azzerati.');
      console.log('Reset result', data);
      setConfirm('');
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore reset');
    } finally { setLoading(false); }
  };

  return (
    <Card className="bg-white border-2 border-red-300 rounded-xl" data-testid="factory-reset-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" /> Azzeramento dati (pre go‑live)
        </CardTitle>
        <CardDescription className="text-red-600">
          Cancella tutti i dati operativi (utenti non‑admin, richieste, ordini, documenti, recensioni, bonifici). Usa solo prima del lancio in produzione.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="reset-keep-admin" checked={keepAdmin} onChange={(e) => setKeepAdmin(e.target.checked)} /> Mantieni account admin</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="reset-keep-site" checked={keepSite} onChange={(e) => setKeepSite(e.target.checked)} /> Mantieni contenuti landing (Personalizza sito)</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="reset-keep-footer" checked={keepFooter} onChange={(e) => setKeepFooter(e.target.checked)} /> Mantieni footer (dati aziendali)</label>
          <label className="flex items-center gap-2"><input type="checkbox" data-testid="reset-keep-settings" checked={keepSettings} onChange={(e) => setKeepSettings(e.target.checked)} /> Mantieni impostazioni (prezzi, IBAN, pacchetti)</label>
        </div>
        <div>
          <Label className="text-red-700">Digita <strong>RESET</strong> per confermare</Label>
          <Input data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" className="rounded-lg border-red-300 mt-1" />
        </div>
        <Button data-testid="factory-reset-btn" onClick={handleReset} disabled={loading || confirm !== 'RESET'} className="bg-red-600 hover:bg-red-700 text-white rounded-lg w-full">
          {loading ? 'Cancellazione in corso...' : '🗑️ Azzera dati operativi'}
        </Button>
      </CardContent>
    </Card>
  );
};

const OpenRequestsSection = ({ requests, onUpdate }) => {
  const open = requests.filter(r => r.stato === 'open');
  const closed = requests.filter(r => r.stato === 'closed');
  const [tab, setTab] = useState('open');
  const list = tab === 'open' ? open : closed;

  const handleHide = async (id) => {
    if (!window.confirm('Rimuovere dalla lista pubblica? Le imprese che hanno già acquistato continueranno a vederla.')) return;
    try {
      await axios.post(`${API}/admin/requests/${id}/hide`, {}, { withCredentials: true });
      toast.success('Richiesta rimossa dalla lista pubblica');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  const handleUnhide = async (id) => {
    try {
      await axios.post(`${API}/admin/requests/${id}/unhide`, {}, { withCredentials: true });
      toast.success('Richiesta ripristinata');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!confirmTwice('RIMOZIONE TOTALE: eliminata anche dalle imprese che l\'hanno comprata. Confermi?')) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { withCredentials: true });
      toast.success('Richiesta eliminata totalmente');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="open-requests-section">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste Approvate</h2>
        <div className="flex gap-2">
          <Button data-testid="filter-open" onClick={() => setTab('open')} size="sm" variant={tab === 'open' ? 'default' : 'outline'} className={tab === 'open' ? 'bg-[#EA580C] text-white' : ''}>
            Aperte ({open.length})
          </Button>
          <Button data-testid="filter-closed" onClick={() => setTab('closed')} size="sm" variant={tab === 'closed' ? 'default' : 'outline'} className={tab === 'closed' ? 'bg-[#EA580C] text-white' : ''}>
            Chiuse ({closed.length})
          </Button>
        </div>
      </div>

      {list.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna richiesta {tab === 'open' ? 'aperta' : 'chiusa'}</p>
          </CardContent>
        </Card>
      ) : (
        list.map((req) => (
          <Card key={req.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-open-request-${req.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle className="text-xl text-[#0F172A]">{req.titolo}</CardTitle>
                  <CardDescription className="mt-1">
                    {req.indirizzo}, {req.cap} {req.citta}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    req.stato === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'
                  }`}>{req.stato === 'open' ? 'Aperta' : 'Chiusa'}</span>
                  {req.hidden_from_public && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Nascosta</span>
                  )}
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" data-testid={`purchases-count-${req.id}`}>
                    {req.purchases_count || 0} acquisti
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4 text-sm">{req.descrizione}</p>
              {req.proprietario && (
                <div className="bg-slate-50 rounded-lg p-3 mb-3 text-sm">
                  <p className="font-medium text-[#0F172A] mb-1">Proprietario</p>
                  <p>{req.proprietario.name} {req.proprietario.cognome || ''}</p>
                  <p className="text-slate-600">{req.proprietario.email} • {req.proprietario.phone}</p>
                </div>
              )}
              {req.purchase_imprese && req.purchase_imprese.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-3 mb-3 text-sm">
                  <p className="font-medium text-[#0F172A] mb-2">Imprese che hanno acquistato</p>
                  <div className="space-y-1">
                    {req.purchase_imprese.map((imp, idx) => (
                      <div key={imp.user_id || imp.id || imp.email || idx} className="flex items-center justify-between text-xs" data-testid={`req-${req.id}-impresa-${idx}`}>
                        <div>
                          <span className="font-medium">{imp.ragione_sociale}</span>
                          <span className="text-slate-600"> • {imp.email} • {imp.phone}</span>
                        </div>
                        <span className="text-slate-500">{imp.credits_spent} crediti</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                {req.hidden_from_public ? (
                  <Button data-testid={`unhide-open-${req.id}`} onClick={() => handleUnhide(req.id)} size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                    Ripristina visibilità
                  </Button>
                ) : (
                  <Button data-testid={`hide-open-${req.id}`} onClick={() => handleHide(req.id)} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                    <XCircle className="w-4 h-4 mr-2" /> Rimuovi dalla lista pubblica
                  </Button>
                )}
                <Button data-testid={`delete-open-${req.id}`} onClick={() => handleDelete(req.id)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-2" /> Rimuovi totalmente
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

const RevenueSection = ({ revenue }) => {
  if (!revenue) {
    return (
      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardContent className="p-12 text-center">
          <Euro className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Dati revenue non disponibili</p>
        </CardContent>
      </Card>
    );
  }

  const fmt = (v) => `€${(v || 0).toFixed(2)}`;
  const typeLabels = { credits: 'Pacchetti crediti', consultation: 'Consulenze', other: 'Altro' };
  const methodLabels = { card: 'Carta (Stripe)', bank_transfer: 'Bonifico bancario' };

  return (
    <div className="space-y-4" data-testid="revenue-section">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Revenue</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Euro className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-500">Revenue totale</p>
            </div>
            <p className="text-3xl font-bold text-[#0F172A]" data-testid="revenue-total">{fmt(revenue.total)}</p>
            <p className="text-xs text-slate-500 mt-1">{revenue.transactions_count} transazioni pagate</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-500">Da pacchetti crediti</p>
            </div>
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="revenue-credits">{fmt(revenue.by_type?.credits)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-500">Da consulenze</p>
            </div>
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="revenue-consultation">{fmt(revenue.by_type?.consultation)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardHeader>
            <CardTitle className="text-lg text-[#0F172A]">Per metodo di pagamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(revenue.by_method || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{methodLabels[k] || k}</span>
                <span className="font-semibold text-[#0F172A]">{fmt(v)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardHeader>
            <CardTitle className="text-lg text-[#0F172A]">Per mese</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-60 overflow-y-auto">
            {(revenue.by_month || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nessun dato</p>
            ) : revenue.by_month.map((m) => (
              <div key={m.month} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{m.month}</span>
                <span className="font-semibold text-[#0F172A]">{fmt(m.amount)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-[#0F172A]">Ultime transazioni pagate</CardTitle>
            <span className="text-xs text-slate-500">{revenue.transactions_count} totali</span>
          </div>
        </CardHeader>
        <CardContent>
          {(revenue.transactions || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Nessuna transazione pagata</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {revenue.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50 text-sm" data-testid={`revenue-txn-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0F172A] truncate">
                      {t.user?.name || t.user?.email || '—'}
                      <span className="text-slate-500 font-normal"> • {typeLabels[t.type] || t.type} • {methodLabels[t.payment_method] || t.payment_method}</span>
                    </p>
                    <p className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('it-IT') : ''}</p>
                  </div>
                  <p className="text-base font-bold text-emerald-700 flex-shrink-0 ml-3">{fmt(t.amount)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Ordini di acquisto emessi</p>
            <p className="text-xs text-slate-400">(non incluso in revenue automatica)</p>
          </div>
          <p className="text-xl font-bold text-[#0F172A]" data-testid="revenue-orders-total">{fmt(revenue.orders_total)} <span className="text-xs font-normal text-slate-500">({revenue.orders_count})</span></p>
        </CardContent>
      </Card>
    </div>
  );
};


const OrderRequestsAdminSection = ({ orderRequests, onUpdate }) => {
  const handleEmit = async (id) => {
    if (!window.confirm('Emettere ordine di acquisto? Verrà generato un bonifico bancario pendente.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/order-requests/${id}/emit`, {}, { withCredentials: true });
      toast.success(`Ordine ${data.purchase_order?.order_number} emesso`);
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const handleReject = async (id) => {
    if (!window.confirm('Rifiutare questa richiesta di ordine?')) return;
    try {
      await axios.post(`${API}/admin/order-requests/${id}/reject`, {}, { withCredentials: true });
      toast.success('Richiesta rifiutata');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="order-requests-section">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste di Emissione Ordine</h2>
      {orderRequests.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <ScrollText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna richiesta di ordine</p>
          </CardContent>
        </Card>
      ) : orderRequests.map((or) => {
        const isCredits = or.kind === 'credits' || !or.kind;
        const who = isCredits
          ? { name: or.impresa_name, email: or.impresa_email, phone: or.impresa_phone, role: 'Impresa' }
          : { name: or.requester_name, email: or.requester_email, phone: or.requester_phone, role: 'Proprietario' };
        const amount = (or.amount_cents ?? or.package_amount_cents ?? 0) / 100;
        return (
        <Card key={or.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`admin-order-request-${or.id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${isCredits ? 'bg-purple-100 text-purple-700' : 'bg-pink-100 text-pink-700'}`}>
                  {isCredits ? 'Crediti Impresa' : 'Consulenza Proprietario'}
                </span>
                <span className="font-mono text-xs text-slate-600">{or.request_number}</span>
              </div>
              <p className="font-medium text-[#0F172A]">{who.name} <span className="text-xs font-normal text-slate-500">({who.role})</span></p>
              <p className="text-xs text-slate-500">{who.email} • {who.phone}</p>
              {isCredits ? (
                <p className="text-sm mt-1">
                  Pacchetto: <strong>{or.package_credits} crediti</strong> {or.package_bonus_pct ? `(+${or.package_bonus_pct}%)` : ''} • <strong>€{amount.toFixed(2)}</strong>
                </p>
              ) : (
                <p className="text-sm mt-1">
                  Consulenza telefonica • <strong>€{amount.toFixed(2)}</strong>
                  {or.consultation_description && <span className="block text-xs text-slate-500 mt-0.5 italic">"{or.consultation_description.slice(0,120)}"</span>}
                </p>
              )}
              {or.order_number && <p className="font-mono text-xs text-[#EA580C] mt-1">PO: {or.order_number}</p>}
              <p className="text-xs text-slate-400 mt-1">{new Date(or.created_at).toLocaleString('it-IT')}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-1 rounded font-medium ${
                or.stato === 'richiesto' ? 'bg-yellow-100 text-yellow-700' :
                or.stato === 'emesso' ? 'bg-blue-100 text-blue-700' :
                or.stato === 'pagato' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`}>{or.stato}</span>
              {or.stato === 'richiesto' && (
                <>
                  <Button data-testid={`emit-order-${or.id}`} onClick={() => handleEmit(or.id)} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-1" /> Emetti
                  </Button>
                  <Button data-testid={`reject-order-${or.id}`} onClick={() => handleReject(or.id)} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                    <XCircle className="w-4 h-4 mr-1" /> Rifiuta
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};

const SupportAdminSection = ({ tickets, onUpdate }) => {
  const handleUpdate = async (id, stato) => {
    try {
      await axios.post(`${API}/admin/support/${id}/update`, { stato }, { withCredentials: true });
      toast.success('Ticket aggiornato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };
  const handleDelete = async (id) => {
    if (!confirmTwice('Eliminare definitivamente questo ticket?')) return;
    try {
      await axios.delete(`${API}/admin/support/${id}`, { withCredentials: true });
      toast.success('Ticket eliminato');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4" data-testid="support-admin-section">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Richieste di Supporto</h2>
      {tickets.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna richiesta di supporto</p>
          </CardContent>
        </Card>
      ) : tickets.map((t) => (
        <Card key={t.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`support-ticket-${t.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold text-[#EA580C]">{t.ticket_number}</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded capitalize">{t.role}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    t.stato === 'aperto' ? 'bg-yellow-100 text-yellow-700' :
                    t.stato === 'in_lavorazione' ? 'bg-blue-100 text-blue-700' :
                    t.stato === 'risolto' ? 'bg-green-100 text-green-700' :
                    'bg-slate-200 text-slate-700'
                  }`}>{t.stato.replace('_', ' ')}</span>
                </div>
                <p className="text-sm font-medium text-[#0F172A] mt-2">{t.oggetto}</p>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{t.messaggio}</p>
              </div>
              <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString('it-IT')}</div>
            </div>
            <div className="bg-slate-50 rounded p-2 text-xs space-y-0.5 mt-2">
              {t.role === 'proprietario' ? (
                <>
                  <p><strong>{t.nome} {t.cognome}</strong> • {t.email} • {t.phone}</p>
                </>
              ) : (
                <>
                  <p><strong>{t.ragione_sociale}</strong> — Ref: {t.referente}</p>
                  <p>P.IVA: {t.partita_iva} • CF: {t.codice_fiscale}</p>
                  <p>Sede: {t.sede_legale}</p>
                  <p>{t.email} • {t.phone}</p>
                </>
              )}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {t.stato !== 'in_lavorazione' && (
                <Button data-testid={`ticket-progress-${t.id}`} onClick={() => handleUpdate(t.id, 'in_lavorazione')} size="sm" variant="outline" className="border-blue-300 text-blue-700">
                  In lavorazione
                </Button>
              )}
              {t.stato !== 'risolto' && (
                <Button data-testid={`ticket-resolve-${t.id}`} onClick={() => handleUpdate(t.id, 'risolto')} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                  Segna risolto
                </Button>
              )}
              {t.stato !== 'chiuso' && (
                <Button data-testid={`ticket-close-${t.id}`} onClick={() => handleUpdate(t.id, 'chiuso')} size="sm" variant="outline" className="border-slate-300">
                  Chiudi
                </Button>
              )}
              <Button data-testid={`ticket-delete-${t.id}`} onClick={() => handleDelete(t.id)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const FooterSettingsPanel = ({ footer, onUpdate }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setData(footer || {}); }, [footer]);

  if (!data) return null;

  const fields = [
    { key: 'intestazione', label: 'Intestazione (Ragione sociale completa)' },
    { key: 'tagline', label: 'Tagline / Descrizione breve' },
    { key: 'partita_iva', label: 'Partita IVA' },
    { key: 'codice_fiscale', label: 'Codice Fiscale' },
    { key: 'sede_legale', label: 'Sede legale' },
    { key: 'pec', label: 'PEC' },
    { key: 'email_contatto', label: 'Email di contatto' },
    { key: 'telefono', label: 'Telefono di contatto' },
    { key: 'iban', label: 'IBAN' },
    { key: 'privacy_url', label: 'URL Privacy Policy' },
    { key: 'terms_url', label: 'URL Termini e Condizioni' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/footer`, data, { withCredentials: true });
      toast.success('Footer aggiornato');
      onUpdate();
    } catch (e) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid="footer-settings-panel">
      <CardHeader>
        <CardTitle className="text-xl text-[#0F172A]">Dati a piè di pagina del sito</CardTitle>
        <CardDescription>Queste informazioni vengono mostrate nel footer della homepage pubblica.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            {f.key === 'tagline' || f.key === 'sede_legale' ? (
              <Textarea
                data-testid={`footer-${f.key}-input`}
                value={data[f.key] || ''}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                rows={2}
                className="rounded-lg"
              />
            ) : (
              <Input
                data-testid={`footer-${f.key}-input`}
                value={data[f.key] || ''}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                className="rounded-lg"
              />
            )}
          </div>
        ))}
        <Button data-testid="save-footer-btn" onClick={handleSave} disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
          {saving ? 'Salvataggio...' : 'Salva dati footer'}
        </Button>
      </CardContent>
    </Card>
  );
};



const SiteContentPanel = ({ site, onUpdate }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => { setData(site || {}); }, [site]);

  if (!data) return null;

  const sections = [
    { label: 'Brand', fields: [
      { key: 'brand_name', label: 'Nome del sito (header e footer)' },
    ]},
    { label: 'Hero (sezione principale)', fields: [
      { key: 'hero_title', label: 'Titolo grande', multiline: true },
      { key: 'hero_subtitle', label: 'Sottotitolo', multiline: true },
      { key: 'hero_cta_proprietario', label: 'Testo bottone Proprietario' },
      { key: 'hero_cta_impresa', label: 'Testo bottone Impresa' },
    ]},
    { label: 'Sezione "Come funziona"', fields: [
      { key: 'how_it_works_eyebrow', label: 'Etichetta sopra il titolo' },
      { key: 'how_it_works_title', label: 'Titolo della sezione' },
      { key: 'step1_title', label: 'Step 1 — Titolo' },
      { key: 'step1_description', label: 'Step 1 — Descrizione', multiline: true },
      { key: 'step2_title', label: 'Step 2 — Titolo' },
      { key: 'step2_description', label: 'Step 2 — Descrizione', multiline: true },
      { key: 'step3_title', label: 'Step 3 — Titolo' },
      { key: 'step3_description', label: 'Step 3 — Descrizione', multiline: true },
    ]},
    { label: 'Sezione Recensioni', fields: [
      { key: 'reviews_eyebrow', label: 'Etichetta sopra il titolo' },
      { key: 'reviews_title', label: 'Titolo della sezione' },
    ]},
    { label: 'Call To Action finale (sfondo scuro)', fields: [
      { key: 'cta_title', label: 'Titolo CTA' },
      { key: 'cta_subtitle', label: 'Sottotitolo CTA', multiline: true },
      { key: 'cta_button_primary', label: 'Testo bottone principale' },
      { key: 'cta_button_support', label: 'Testo bottone supporto' },
    ]},
    { label: 'Sezione Consulenza Telefonica (Landing)', fields: [
      { key: 'consult_promo_eyebrow', label: 'Etichetta sopra il titolo' },
      { key: 'consult_promo_title', label: 'Titolo della sezione', multiline: true },
      { key: 'consult_promo_description', label: 'Descrizione', multiline: true },
      { key: 'consult_promo_bullet1', label: 'Bullet 1' },
      { key: 'consult_promo_bullet2', label: 'Bullet 2' },
      { key: 'consult_promo_bullet3', label: 'Bullet 3' },
      { key: 'consult_promo_bullet4', label: 'Bullet 4' },
      { key: 'consult_promo_cta', label: 'Testo bottone CTA' },
      { key: 'consult_card_title', label: 'Card laterale — Titolo' },
      { key: 'consult_card_duration', label: 'Card — Durata' },
      { key: 'consult_card_payment', label: 'Card — Pagamento' },
      { key: 'consult_card_availability', label: 'Card — Disponibilità' },
      { key: 'consult_card_language', label: 'Card — Lingua' },
      { key: 'consult_card_howto', label: 'Card — "Come funziona" (testo)', multiline: true },
    ]},
    { label: 'Banner Consulenza (Dashboard Proprietario)', fields: [
      { key: 'prop_banner_eyebrow', label: 'Etichetta sopra il titolo' },
      { key: 'prop_banner_title', label: 'Titolo banner', multiline: true },
      { key: 'prop_banner_description', label: 'Descrizione banner', multiline: true },
      { key: 'prop_banner_cta', label: 'Testo bottone CTA' },
    ]},
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, data, { withCredentials: true });
      toast.success('Personalizzazione salvata. Ricarica la landing per vedere le modifiche.');
      onUpdate();
    } catch (e) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Ripristinare TUTTI i contenuti del sito ai valori di default?')) return;
    try {
      const { data: fresh } = await axios.post(`${API}/admin/site-content/reset`, {}, { withCredentials: true });
      setData(fresh);
      toast.success('Contenuti ripristinati');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Massimo 8MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('field', 'hero_image_url');
      const { data: result } = await axios.post(`${API}/admin/site-content/upload-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setData((prev) => ({ ...prev, hero_image_url: result.hero_image_url }));
      toast.success('Immagine caricata');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const heroPreview = data.hero_image_url
    ? (data.hero_image_url.startsWith('/api/')
        ? `${process.env.REACT_APP_BACKEND_URL}${data.hero_image_url}`
        : data.hero_image_url)
    : '';

  return (
    <div className="space-y-6" data-testid="site-content-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-[#0F172A]">Personalizza il sito pubblico</h2>
          <p className="text-sm text-slate-600">Modifica testi, immagini e colori della pagina principale.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="site-reset-btn" onClick={handleReset} variant="outline" className="border-slate-300">
            Ripristina default
          </Button>
          <Button data-testid="site-save-btn" onClick={handleSave} disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white">
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg text-[#0F172A]">Immagine Hero principale</CardTitle>
          <CardDescription>Mostrata accanto al titolo nella parte alta della landing. Max 8MB. JPG, PNG, WebP, GIF.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {heroPreview && (
            <div className="w-full max-w-md">
              <img src={heroPreview} alt="Hero preview" className="rounded-lg w-full border border-slate-200" data-testid="hero-image-preview" />
            </div>
          )}
          <input
            ref={fileInputRef}
            data-testid="hero-image-file-input"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading}
            className="block text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#EA580C] file:text-white hover:file:bg-[#EA580C]/90 cursor-pointer"
          />
          {uploading && <p className="text-xs text-slate-500">Caricamento in corso...</p>}
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-xl">
        <CardHeader>
          <CardTitle className="text-lg text-[#0F172A]">Colori</CardTitle>
          <CardDescription>L'accento colora bottoni e link principali; lo sfondo scuro è usato nella sezione CTA finale e nei titoli.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Colore accent (default arancione)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="color-accent-picker"
                type="color"
                value={data.accent_color || '#EA580C'}
                onChange={(e) => setData({ ...data, accent_color: e.target.value })}
                className="h-10 w-16 rounded cursor-pointer border border-slate-200"
              />
              <Input
                data-testid="color-accent-input"
                value={data.accent_color || ''}
                onChange={(e) => setData({ ...data, accent_color: e.target.value })}
                placeholder="#EA580C"
                className="rounded-lg font-mono"
              />
            </div>
          </div>
          <div>
            <Label>Colore scuro (sfondo CTA, titoli)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="color-dark-picker"
                type="color"
                value={data.dark_color || '#0F172A'}
                onChange={(e) => setData({ ...data, dark_color: e.target.value })}
                className="h-10 w-16 rounded cursor-pointer border border-slate-200"
              />
              <Input
                data-testid="color-dark-input"
                value={data.dark_color || ''}
                onChange={(e) => setData({ ...data, dark_color: e.target.value })}
                placeholder="#0F172A"
                className="rounded-lg font-mono"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {sections.map((sec) => (
        <Card key={sec.label} className="bg-white border border-slate-200 rounded-xl">
          <CardHeader>
            <CardTitle className="text-lg text-[#0F172A]">{sec.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sec.fields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                {f.multiline ? (
                  <Textarea
                    data-testid={`site-${f.key}-input`}
                    value={data[f.key] || ''}
                    onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                    rows={2}
                    className="rounded-lg"
                  />
                ) : (
                  <Input
                    data-testid={`site-${f.key}-input`}
                    value={data[f.key] || ''}
                    onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                    className="rounded-lg"
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end gap-2 sticky bottom-4">
        <Button data-testid="site-save-btn-bottom" onClick={handleSave} disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white shadow-lg">
          {saving ? 'Salvataggio...' : 'Salva modifiche'}
        </Button>
      </div>
    </div>
  );
};



const DocumentsAdminSection = ({ docs, onUpdate }) => {
  return (
    <div className="space-y-4" data-testid="documents-admin-section">
      <h2 className="text-2xl font-semibold text-[#0F172A]">Documenti da Verificare</h2>
      {docs.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessun documento in attesa di verifica</p>
          </CardContent>
        </Card>
      ) : docs.map((d) => <PendingDocRow key={d.id} d={d} onUpdate={onUpdate} />)}
    </div>
  );
};

const PendingDocRow = ({ d, onUpdate }) => {
  const initialDate = d.validation_date ? new Date(d.validation_date).toISOString().slice(0, 10) : '';
  const [validationDate, setValidationDate] = useState(initialDate);
  const [saving, setSaving] = useState(false);
  const viewUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/${id}/file`;

  // Default validity months (matches backend rule: DURC=6, others=12)
  const validityMonths = d.validity_months || ((d.tipo || '').toUpperCase().startsWith('DURC') ? 6 : 12);
  const previewExpiry = (() => {
    if (!validationDate) return null;
    const dt = new Date(validationDate);
    dt.setMonth(dt.getMonth() + validityMonths);
    return dt;
  })();

  const handleApprove = async () => {
    if (!validationDate) { toast.error('Imposta la data di inizio validità'); return; }
    if (!confirmTwice(
      `Approvare "${d.tipo}" con data di inizio ${new Date(validationDate).toLocaleDateString('it-IT')}? La scadenza sarà ricalcolata a ${previewExpiry?.toLocaleDateString('it-IT')}.`,
      '⚠️ Ultima conferma: approvi questo documento?'
    )) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/documents/${d.id}/approve`,
        { validation_date: validationDate },
        { withCredentials: true });
      toast.success('Documento approvato — date aggiornate');
      onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    const note = window.prompt('Motivo del rifiuto (sarà inviato all\'impresa via email):');
    if (note === null) return;
    if (!confirmTwice(
      `Rifiutare "${d.tipo}"? L'impresa riceverà la motivazione: "${note}"`,
      '⚠️ Ultima conferma: vuoi rifiutare questo documento?'
    )) return;
    try {
      await axios.post(`${API}/admin/documents/${d.id}/reject`, { note }, { withCredentials: true });
      toast.success('Documento rifiutato — impresa notificata');
      onUpdate();
    } catch (e) { toast.error('Errore'); }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`pending-doc-${d.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#0F172A]">
              {d.tipo}
              <span className="text-xs ml-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{validityMonths} mesi validità</span>
            </p>
            {d.impresa && (
              <p className="text-sm text-slate-700 mt-1">
                <strong>{d.impresa.ragione_sociale}</strong> — {d.impresa.email} — {d.impresa.phone}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-1 truncate">{d.original_filename}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Data inserita dall'impresa: {initialDate ? new Date(initialDate).toLocaleDateString('it-IT') : '—'}
            </p>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Data inizio validità (modificabile)</Label>
            <Input
              data-testid={`doc-validation-date-${d.id}`}
              type="date"
              value={validationDate}
              onChange={(e) => setValidationDate(e.target.value)}
              className="rounded-lg"
            />
          </div>
          <div>
            <Label className="text-xs">Scadenza calcolata</Label>
            <div data-testid={`doc-expiry-preview-${d.id}`} className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700">
              {previewExpiry ? previewExpiry.toLocaleDateString('it-IT') : '—'}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={viewUrl(d.id)}
            target="_blank"
            rel="noreferrer"
            data-testid={`view-doc-${d.id}`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >
            Apri documento
          </a>
          <Button data-testid={`approve-doc-${d.id}`} onClick={handleApprove} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
            <CheckCircle className="w-4 h-4 mr-2" /> {saving ? 'Approvazione...' : 'Approva'}
          </Button>
          <Button data-testid={`reject-doc-${d.id}`} onClick={handleReject} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-2" /> Rifiuta
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


const PaidConsultationsSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/purchase-orders/paid-consultations`, { withCredentials: true });
      setItems(data);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-slate-500">Caricamento...</p>;
  return (
    <div className="space-y-4" data-testid="paid-consultations-section">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Consulenze pagate</h2>
        <p className="text-sm text-slate-600">Sottocategoria di "Ordini" — solo PO consulenza con bonifico confermato.</p>
      </div>
      {items.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessuna consulenza pagata</p>
          </CardContent>
        </Card>
      ) : items.map((it) => {
        const prop = it.proprietario || {};
        const c = it.consultation || {};
        return (
          <Card key={it.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`paid-cons-${it.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono text-sm text-[#EA580C]">{it.order_number}</p>
                  <p className="font-medium text-[#0F172A]">{prop.name} {prop.cognome}</p>
                  <p className="text-xs text-slate-500">{prop.email} • {prop.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700">€{((it.importo_cents || 0) / 100).toFixed(2)}</p>
                  <p className="text-xs text-slate-500">Pagato: {it.paid_at ? new Date(it.paid_at).toLocaleString('it-IT') : '—'}</p>
                </div>
              </div>
              {c.descrizione && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-xs text-slate-600 mb-1">Problema descritto</p>
                  <p className="text-slate-700">{c.descrizione}</p>
                </div>
              )}
              {(c.indirizzo_residenza || c.codice_fiscale) && (
                <div className="bg-blue-50 rounded-lg p-3 text-xs text-slate-700 space-y-0.5">
                  <p className="font-semibold text-slate-600 mb-1">Dati anagrafici</p>
                  {c.indirizzo_residenza && <p>{c.indirizzo_residenza} {c.civico}, {c.cap_residenza} {c.citta_residenza}</p>}
                  {c.codice_fiscale && <p>CF: <span className="font-mono">{c.codice_fiscale}</span></p>}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const InactiveUsersSection = ({ months, role }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const load = async () => {
    try {
      const qs = `months=${months}${role ? `&role=${role}` : ''}`;
      const { data } = await axios.get(`${API}/admin/users/inactive?${qs}`, { withCredentials: true });
      setItems(data);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [months, role]);

  const handleReactivate = async (u) => {
    setSending(u.id);
    try {
      await axios.post(`${API}/admin/users/${u.id}/send-reactivation`, {}, { withCredentials: true });
      toast.success(`Email di riattivazione inviata a ${u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSending(null); }
  };

  const handleImpersonate = async (u) => {
    try {
      await axios.post(`${API}/admin/impersonate/${u.id}`, {}, { withCredentials: true });
      await checkAuth();
      toast.success(`Navigando come ${u.name || u.email}`);
      navigate('/dashboard');
    } catch (e) { toast.error('Errore'); }
  };

  const handleDelete = async (u) => {
    if (!confirmTwice(`Eliminare definitivamente "${u.name || u.email}"? Verranno cancellati anche i dati collegati.`)) return;
    if (window.prompt('Per confermare digita "ELIMINA":') !== 'ELIMINA') { toast.info('Annullato'); return; }
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, { withCredentials: true });
      toast.success('Utente spostato nel cestino — ripristinabile dalla tab "Cestino"');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (loading) return <p className="text-slate-500">Caricamento...</p>;
  return (
    <div className="space-y-4" data-testid={`inactive-users-section-${months}`}>
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Utenti inattivi da oltre {months} mesi</h2>
        <p className="text-sm text-slate-600">
          Utenti (proprietari + imprese) che non accedono da più di {months} mesi. Puoi inviare email di riattivazione, navigare come loro o eliminarli.
        </p>
      </div>
      {items.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nessun utente inattivo da {months}+ mesi</p>
          </CardContent>
        </Card>
      ) : items.map((u) => (
        <Card key={u.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`inactive-${months}-${u.id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${u.role === 'impresa' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {u.role}
                </span>
                <p className="font-medium text-[#0F172A]">{u.ragione_sociale || u.name}</p>
              </div>
              <p className="text-xs text-slate-500">{u.email} • {u.phone}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {u.last_login_at
                  ? <>Ultimo accesso: <strong>{new Date(u.last_login_at).toLocaleString('it-IT')}</strong></>
                  : <span className="italic">Mai effettuato l'accesso (registrato {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : ''})</span>}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                data-testid={`reactivate-${u.id}`}
                size="sm"
                disabled={sending === u.id}
                onClick={() => handleReactivate(u)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Mail className="w-3 h-3 mr-1" /> {sending === u.id ? 'Invio...' : 'Email riattivazione'}
              </Button>
              <Button data-testid={`inactive-impersonate-${u.id}`} onClick={() => handleImpersonate(u)} size="sm" variant="outline" className="border-blue-300 text-blue-700">
                <LogIn className="w-3 h-3 mr-1" /> Naviga come
              </Button>
              <Button data-testid={`inactive-delete-${u.id}`} onClick={() => handleDelete(u)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3 h-3 mr-1" /> Elimina
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


const DynamicTabsList = ({ navConfig, counts, activeTab, setActiveTab }) => {
  if (!navConfig) {
    return (
      <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="admin-tabs">
        <TabsTrigger value="requests" data-testid="tab-requests">Caricamento layout...</TabsTrigger>
      </TabsList>
    );
  }
  const labelWithCount = (label, key) => counts[key] != null ? `${label} (${counts[key]})` : label;
  const activeFolder = navConfig.find(n => n.type === 'folder' && (n.children || []).some(c => c.tab_key === activeTab));
  return (
    <div className="space-y-2">
      <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="admin-tabs">
        {navConfig.map((n) => {
          if (n.type === 'tab') {
            return (
              <TabsTrigger key={n.id} value={n.tab_key} data-testid={`tab-${n.tab_key}`}>
                {labelWithCount(n.label, n.tab_key)}
              </TabsTrigger>
            );
          }
          // Folder pseudo-trigger: clicking activates its first child
          const kids = (n.children || []).filter(c => c.type === 'tab');
          const isActive = kids.some(c => c.tab_key === activeTab);
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => { if (kids[0]) setActiveTab(kids[0].tab_key); }}
              data-testid={`folder-trigger-${n.id}`}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${isActive ? 'bg-[#EA580C] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
            >
              📁 {n.label}
            </button>
          );
        })}
        <TabsTrigger value="layout" data-testid="tab-layout">⚙️ Layout</TabsTrigger>
      </TabsList>

      {activeFolder && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 flex flex-wrap gap-1" data-testid={`folder-children-${activeFolder.id}`}>
          {(activeFolder.children || []).filter(c => c.type === 'tab').map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveTab(c.tab_key)}
              data-testid={`subtab-${c.tab_key}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-all ${activeTab === c.tab_key ? 'bg-white text-[#EA580C] shadow-sm font-medium' : 'text-slate-600 hover:bg-white/60'}`}
            >
              {labelWithCount(c.label, c.tab_key)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const NavConfigEditor = ({ initialTree, onSave }) => {
  const [tree, setTree] = useState(initialTree || []);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setTree(initialTree || []); }, [initialTree]);

  const persist = async (newTree) => {
    setTree(newTree);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/nav-config`, { tree }, { withCredentials: true });
      toast.success('Layout salvato');
      onSave(data.tree);
      setTree(data.tree);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };
  const resetDefault = async () => {
    if (!window.confirm('Ripristinare il layout di default?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/nav-config/reset`, {}, { withCredentials: true });
      toast.success('Layout ripristinato');
      onSave(data.tree);
      setTree(data.tree);
    } catch (e) { toast.error('Errore'); }
  };

  // Operations
  const moveItem = (idxPath, direction) => {
    // idxPath: [i] for root level, [i, j] for folder child
    const newTree = JSON.parse(JSON.stringify(tree));
    if (idxPath.length === 1) {
      const [i] = idxPath;
      const target = i + direction;
      if (target < 0 || target >= newTree.length) return;
      [newTree[i], newTree[target]] = [newTree[target], newTree[i]];
    } else {
      const [i, j] = idxPath;
      const kids = newTree[i].children || [];
      const target = j + direction;
      if (target < 0 || target >= kids.length) return;
      [kids[j], kids[target]] = [kids[target], kids[j]];
    }
    persist(newTree);
  };
  const renameNode = (idxPath, newLabel) => {
    const newTree = JSON.parse(JSON.stringify(tree));
    if (idxPath.length === 1) newTree[idxPath[0]].label = newLabel;
    else newTree[idxPath[0]].children[idxPath[1]].label = newLabel;
    persist(newTree);
  };
  const addFolder = () => {
    const newTree = JSON.parse(JSON.stringify(tree));
    newTree.push({ id: `folder-${Date.now()}`, type: 'folder', label: 'Nuova cartella', children: [] });
    persist(newTree);
  };
  const deleteFolder = (idx) => {
    if (!confirmTwice('Eliminare la cartella? I suoi elementi torneranno al livello principale.')) return;
    const newTree = JSON.parse(JSON.stringify(tree));
    const folder = newTree[idx];
    const kids = folder.children || [];
    newTree.splice(idx, 1, ...kids);
    persist(newTree);
  };
  const moveIntoFolder = (rootIdx, folderId) => {
    const newTree = JSON.parse(JSON.stringify(tree));
    const item = newTree[rootIdx];
    const folder = newTree.find(n => n.id === folderId);
    if (!folder || folder.type !== 'folder' || item.type !== 'tab') return;
    folder.children = folder.children || [];
    folder.children.push(item);
    newTree.splice(rootIdx, 1);
    persist(newTree);
  };
  const moveOutOfFolder = (folderIdx, childIdx) => {
    const newTree = JSON.parse(JSON.stringify(tree));
    const folder = newTree[folderIdx];
    const child = folder.children[childIdx];
    folder.children.splice(childIdx, 1);
    newTree.push(child);
    persist(newTree);
  };

  const folders = tree.filter(n => n.type === 'folder');

  return (
    <div className="space-y-4" data-testid="layout-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-semibold text-[#0F172A]">Layout Dashboard</h2>
          <p className="text-sm text-slate-600">Ordina i tab a piacimento, crea cartelle e sposta i tab al loro interno.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={addFolder} data-testid="add-folder-btn" variant="outline" className="border-slate-300">
            <FolderOpen className="w-4 h-4 mr-2" /> Nuova cartella
          </Button>
          <Button onClick={resetDefault} data-testid="reset-layout-btn" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
            Ripristina default
          </Button>
          <Button onClick={save} data-testid="save-layout-btn" disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white">
            {saving ? 'Salvataggio...' : 'Salva layout'}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {tree.map((n, i) => (
          <Card key={n.id} className={`bg-white border ${n.type === 'folder' ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200'} rounded-xl`} data-testid={`layout-node-${n.id}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded text-slate-600">
                  {n.type === 'folder' ? '📁 CARTELLA' : '📄 TAB'}
                </span>
                <Input
                  data-testid={`label-input-${n.id}`}
                  value={n.label}
                  onChange={(e) => renameNode([i], e.target.value)}
                  className="rounded-lg flex-1 min-w-[150px]"
                />
                <Button size="sm" variant="outline" data-testid={`up-${n.id}`} onClick={() => moveItem([i], -1)} disabled={i === 0}>↑</Button>
                <Button size="sm" variant="outline" data-testid={`down-${n.id}`} onClick={() => moveItem([i], 1)} disabled={i === tree.length - 1}>↓</Button>
                {n.type === 'tab' && folders.length > 0 && (
                  <select
                    data-testid={`move-into-${n.id}`}
                    value=""
                    onChange={(e) => { if (e.target.value) moveIntoFolder(i, e.target.value); }}
                    className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white"
                  >
                    <option value="">Sposta in cartella...</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                )}
                {n.type === 'folder' && (
                  <Button size="sm" variant="outline" data-testid={`delete-folder-${n.id}`} onClick={() => deleteFolder(i)} className="border-red-200 text-red-600">
                    Elimina cartella
                  </Button>
                )}
              </div>
              {n.type === 'folder' && (
                <div className="pl-6 space-y-1 border-l-2 border-blue-200 ml-2">
                  {(n.children || []).length === 0 && (
                    <p className="text-xs italic text-slate-500 py-2">Cartella vuota — sposta dei tab qui dentro dal menù a tendina sopra.</p>
                  )}
                  {(n.children || []).map((c, j) => (
                    <div key={c.id} className="flex items-center gap-2 flex-wrap py-1" data-testid={`folder-child-${c.id}`}>
                      <span className="text-xs text-blue-700">↳ 📄</span>
                      <Input
                        value={c.label}
                        onChange={(e) => renameNode([i, j], e.target.value)}
                        className="rounded-lg flex-1 min-w-[150px] h-8 text-sm"
                      />
                      <Button size="sm" variant="outline" onClick={() => moveItem([i, j], -1)} disabled={j === 0}>↑</Button>
                      <Button size="sm" variant="outline" onClick={() => moveItem([i, j], 1)} disabled={j === (n.children || []).length - 1}>↓</Button>
                      <Button size="sm" variant="outline" onClick={() => moveOutOfFolder(i, j)} className="border-slate-300">← Esci</Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <p className="text-xs text-slate-500 italic">💡 Le modifiche sono applicate solo dopo aver cliccato "Salva layout". Il tab "⚙️ Layout" è sempre disponibile e non può essere spostato.</p>
    </div>
  );
};

const TrashSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/trash`, { withCredentials: true });
      setItems(data);
    } catch (e) { toast.error('Errore caricamento cestino'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleRestore = async (u) => {
    const label = u.ragione_sociale || `${u.name || ''} ${u.cognome || ''}`.trim() || u.email;
    if (!confirmTwice(
      `Ripristinare "${label}"? Tornerà attivo e potrà accedere normalmente.`,
      '⚠️ Ultima conferma: ripristinare questo utente?'
    )) return;
    try {
      await axios.post(`${API}/admin/trash/${u.id}/restore`, {}, { withCredentials: true });
      toast.success(`"${label}" ripristinato`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handlePermanentDelete = async (u) => {
    const label = u.ragione_sociale || `${u.name || ''} ${u.cognome || ''}`.trim() || u.email;
    if (!confirmTwice(
      `🔥 ELIMINAZIONE DEFINITIVA di "${label}".\n\nVerranno cancellati TUTTI i dati collegati: documenti, richieste, ordini, acquisti, recensioni. Lo storico transazioni di pagamento sarà conservato per audit.\n\nQuesta operazione NON può essere annullata.`,
      `⚠️ ULTIMA CONFERMA: stai per eliminare DEFINITIVAMENTE "${label}". Sei davvero sicuro?`
    )) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}?permanent=true`, { withCredentials: true });
      toast.success(`"${label}" eliminato definitivamente`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (loading) return <p className="text-slate-500">Caricamento...</p>;
  return (
    <div className="space-y-4" data-testid="trash-section">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">🗑️ Cestino</h2>
        <p className="text-sm text-slate-600">
          Utenti e aziende spostati nel cestino. Da qui puoi <strong>ripristinarli</strong> oppure <strong>eliminarli definitivamente</strong> (irreversibile).
        </p>
      </div>
      {items.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-xl">
          <CardContent className="p-12 text-center">
            <Trash2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Cestino vuoto</p>
          </CardContent>
        </Card>
      ) : items.map((u) => {
        const label = u.ragione_sociale || `${u.name || ''} ${u.cognome || ''}`.trim() || u.email;
        return (
          <Card key={u.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`trash-${u.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${u.role === 'impresa' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {u.role}
                  </span>
                  <p className="font-medium text-[#0F172A]">{label}</p>
                </div>
                <p className="text-xs text-slate-500">{u.email} • {u.phone || '—'}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Spostato nel cestino il <strong>{new Date(u.deleted_at).toLocaleString('it-IT')}</strong>
                </p>
                {u.last_login_at && (
                  <p className="text-xs text-slate-400 mt-0.5">Ultimo accesso: {new Date(u.last_login_at).toLocaleString('it-IT')}</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button data-testid={`restore-${u.id}`} size="sm" onClick={() => handleRestore(u)} className="bg-green-600 hover:bg-green-700 text-white">
                  ↩️ Ripristina
                </Button>
                <Button data-testid={`permanent-delete-${u.id}`} size="sm" variant="outline" onClick={() => handlePermanentDelete(u)} className="border-red-400 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-3 h-3 mr-1" /> Elimina definitivamente
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminDashboard;
